import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Quote, ArrowRight } from 'lucide-react';
import { testimonialsService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await testimonialsService.getAll();
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Erreur lors du chargement des témoignages:', error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner size="lg" text="Chargement..." />
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Témoignages
            </h1>
            <p className="text-xl text-dark-300">
              Découvrez ce que nos clients disent de nous
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section bg-dark-50 py-16">
        <div className="container-custom">
          {testimonials.length === 0 ? (
            <div className="text-center py-20">
              <Quote className="w-16 h-16 mx-auto mb-4 text-dark-300" />
              <h3 className="text-2xl font-semibold text-dark-900 mb-2">
                Aucun témoignage pour le moment
              </h3>
              <p className="text-dark-500">
                Les témoignages seront affichés ici une fois publiés depuis le panel admin.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="bg-white rounded-2xl p-6 border border-dark-100 h-full">
                    {/* Étoiles */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    
                    {/* Témoignage */}
                    <p className="text-dark-600 mb-6 leading-relaxed text-sm">
                      "{testimonial.testimonial || testimonial.content}"
                    </p>
                    
                    {/* Auteur */}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-dark-200 rounded-full flex items-center justify-center overflow-hidden">
                        {testimonial.image_url ? (
                          <img 
                            src={testimonial.image_url} 
                            alt={testimonial.client_name || testimonial.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-dark-600 font-semibold">
                            {(testimonial.client_name || testimonial.name)?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-dark-900 text-sm">
                          {testimonial.client_name || testimonial.name}
                        </p>
                        <p className="text-xs text-dark-500">
                          {testimonial.position}
                          {testimonial.company && <><br />{testimonial.company}</>}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-dark-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dark-900 mb-6">
              Rejoignez nos clients satisfaits
            </h2>
            <p className="text-dark-500 mb-8">
              Demandez un devis gratuit et découvrez comment nous pouvons transformer votre institution financière.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-dark-900 text-white rounded-lg hover:bg-dark-800 transition-colors font-medium"
            >
              Demande de devis
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default TestimonialsPage;